import type { TaskAssignee } from '@hygge/shared';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { cn } from '@/lib/utils';

const SIZE = {
  xs: { avatar: 'h-4 w-4', fallback: 'text-[8px]', empty: 'h-4 w-4' },
  sm: { avatar: 'h-5 w-5', fallback: 'text-[9px]', empty: 'h-5 w-5' },
} as const;

export interface AssigneeStackProps {
  /**
   * The assignee set to render. `TaskAssignee` and `BoardPersonMini` share this
   * shape, so both card and overview issue rows can pass their `assignees`.
   */
  people: TaskAssignee[];
  /** Avatar sizing preset. Defaults to `sm`. */
  size?: 'xs' | 'sm';
  /** Max avatars shown before a "+N" overflow badge. Defaults to 3. */
  max?: number;
  className?: string;
}

/**
 * Overlapping avatar stack for a task's assignee set. Presentational only —
 * no data fetching, no mutations. Renders a dashed-circle placeholder when
 * nobody is assigned (matching the board card's unassigned look). Names surface
 * via tooltip on hover; the overflow badge lists the remaining names.
 */
export function AssigneeStack({ people, size = 'sm', max = 3, className }: AssigneeStackProps) {
  const formatName = useEmployeeNameFormatter();
  const s = SIZE[size];

  if (people.length === 0) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                'inline-block rounded-full border border-dashed border-border',
                s.empty,
                className,
              )}
            />
          </TooltipTrigger>
          <TooltipContent side="top">Unassigned</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const shown = people.slice(0, max);
  const extra = people.length - shown.length;

  return (
    <TooltipProvider delayDuration={300}>
      <span className={cn('inline-flex -space-x-1.5', className)}>
        {shown.map((p) => (
          <Tooltip key={p.id}>
            <TooltipTrigger asChild>
              <Avatar className={cn('ring-2 ring-background', s.avatar)}>
                <AvatarImage src={p.avatarUrl || undefined} />
                <AvatarFallback className={cn('bg-muted', s.fallback)}>
                  {p.firstName[0]}
                  {p.lastName[0]}
                </AvatarFallback>
              </Avatar>
            </TooltipTrigger>
            <TooltipContent side="top">
              {formatName({ firstName: p.firstName, lastName: p.lastName })}
            </TooltipContent>
          </Tooltip>
        ))}
        {extra > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  'flex items-center justify-center rounded-full bg-muted font-medium text-muted-foreground ring-2 ring-background',
                  s.avatar,
                  s.fallback,
                )}
              >
                +{extra}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              {people
                .slice(max)
                .map((p) => formatName({ firstName: p.firstName, lastName: p.lastName }))
                .join(', ')}
            </TooltipContent>
          </Tooltip>
        )}
      </span>
    </TooltipProvider>
  );
}
